import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  User,
  Stethoscope,
  FileText,
  CheckCircle,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/apiClient';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { Button } from '@/components/ui/button';

interface BookingProposal {
  patientId: string;
  patientName?: string;
  providerId?: string;
  providerName?: string;
  date: string;
  time: string;
  duration?: number;
  reason?: string;
  appointmentType?: string;
}

interface BookingConfirmationCardProps {
  proposal: BookingProposal;
  onConfirmed?: () => void;
  onCancelled?: () => void;
}

export const BookingConfirmationCard: React.FC<BookingConfirmationCardProps> = ({
  proposal,
  onConfirmed,
  onCancelled,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { navigateTo } = useAppNavigation();

  if (isDismissed) return null;

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await apiClient.post('/appointments', {
        patientId: proposal.patientId,
        providerId: proposal.providerId,
        date: proposal.date,
        time: proposal.time,
        duration: proposal.duration || 30,
        reason: proposal.reason || '',
        appointmentType: proposal.appointmentType || 'consultation',
        status: 'scheduled',
      });

      setIsConfirmed(true);
      toast.success('Appointment booked successfully');
      onConfirmed?.();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Failed to book appointment');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleCancel = () => {
    setIsDismissed(true);
    onCancelled?.();
  };

  const handleViewSchedule = () => {
    navigateTo('schedule');
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string) => {
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch {
      return timeStr;
    }
  };

  if (isConfirmed) {
    return (
      <div
        className="rounded-lg border-2 p-4"
        style={{
          backgroundColor: 'var(--color-success-light, #f0fdf4)',
          borderColor: 'var(--color-success, #22c55e)',
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success, #22c55e)' }} />
          <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Appointment Confirmed
          </span>
        </div>
        <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
          {proposal.patientName && `${proposal.patientName} - `}
          {formatDate(proposal.date)} at {formatTime(proposal.time)}
        </p>
        <Button
          variant="link"
          size="xs"
          onClick={handleViewSchedule}
        >
          View in Schedule
        </Button>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border-2 p-4"
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        borderColor: 'var(--color-primary-600)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5" style={{ color: 'var(--color-primary-600)' }} />
          <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Booking Proposal
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={handleCancel}
          aria-label="Dismiss"
        >
          <X />
        </Button>
      </div>

      {/* Details */}
      <div className="space-y-2 mb-4">
        {proposal.patientName && (
          <div className="flex items-center gap-2 text-sm">
            <User className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
            <span style={{ color: 'var(--color-text-secondary)' }}>Patient:</span>
            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {proposal.patientName}
            </span>
          </div>
        )}

        {proposal.providerName && (
          <div className="flex items-center gap-2 text-sm">
            <Stethoscope className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
            <span style={{ color: 'var(--color-text-secondary)' }}>Provider:</span>
            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {proposal.providerName}
            </span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>Date:</span>
          <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
            {formatDate(proposal.date)}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>Time:</span>
          <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
            {formatTime(proposal.time)} ({proposal.duration || 30} min)
          </span>
        </div>

        {proposal.reason && (
          <div className="flex items-center gap-2 text-sm">
            <FileText className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
            <span style={{ color: 'var(--color-text-secondary)' }}>Reason:</span>
            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {proposal.reason}
            </span>
          </div>
        )}

        {proposal.appointmentType && (
          <div className="flex items-center gap-2 text-sm">
            <FileText className="w-4 h-4" style={{ color: 'var(--color-text-tertiary)' }} />
            <span style={{ color: 'var(--color-text-secondary)' }}>Type:</span>
            <span className="font-medium capitalize" style={{ color: 'var(--color-text-primary)' }}>
              {proposal.appointmentType}
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={handleConfirm}
          loading={isConfirming}
          className="flex-1"
        >
          {!isConfirming && <CheckCircle />}
          {isConfirming ? 'Booking...' : 'Confirm Booking'}
        </Button>
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isConfirming}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default BookingConfirmationCard;
